import type { JSX } from 'preact';

const NON_NUMERIC_CHARS = /[^0-9.]/g;

export type DataAttributes = {
  output?: 'decimalFraction' | 'exact';
  maximumFractionDigits?: number;
};

export const Events = {
  beforeinput: handleBeforeInput,
  input: handleInput,
  blur: handleBlur,
};

export function getDataAttributes({
  output = 'decimalFraction',
  maximumFractionDigits = 3,
}: DataAttributes) {
  return {
    'data-output': output,
    'data-maximum-fraction-digits': maximumFractionDigits,
    placeholder: '%',
  };
}

function handleBeforeInput(e: JSX.TargetedEvent<HTMLInputElement>) {
  const { currentTarget } = e;
  // set caret position before %
  if (currentTarget.selectionStart >= currentTarget.value.length) {
    const pos = currentTarget.value.length - 1;
    currentTarget.setSelectionRange(pos, pos);
  }
}

function handleBlur(e: JSX.TargetedEvent<HTMLInputElement>) {
  const { currentTarget } = e;
  const { value } = currentTarget;

  if (!value) {
    return;
  } else if (value.endsWith('.%')) {
    currentTarget.value = value.replace('.', '');
  }
}

function handleInput(e: JSX.TargetedEvent<HTMLInputElement, InputEvent>) {
  const { currentTarget } = e;
  const { nextElementSibling, value } = currentTarget;
  const hiddenField = nextElementSibling as HTMLInputElement;
  const maximumFractionDigits = +currentTarget.dataset.maximumFractionDigits;
  const isDecimalFraction = currentTarget.dataset.output === 'decimalFraction';
  // @ts-ignore event can be a custom event with detail object
  const isInitialRender = !!e.detail?.isInitialRender;
  let cleanedValue = value.replace(NON_NUMERIC_CHARS, '') || '';
  if (cleanedValue.startsWith('.')) {
    cleanedValue = `0${cleanedValue}`;
  }
  let rawNumber = Number(cleanedValue);

  // on intial mount/render ensure the output is correct
  if (isInitialRender && isDecimalFraction) {
    if (cleanedValue) {
      rawNumber = precisionRound(rawNumber * 100, cleanedValue.length);
      cleanedValue = rawNumber.toString();
    }
  }

  // eslint-disable-next-line prefer-const
  let [wholeNumber = '', fraction = ''] = cleanedValue.split('.');

  // enforce fraction maximum
  if (fraction) {
    fraction = fraction.substring(0, maximumFractionDigits);
    cleanedValue = `${wholeNumber}.${fraction}`;
    rawNumber = Number(cleanedValue);
  }

  if (!cleanedValue) {
    currentTarget.value = '';
    hiddenField.value = '';
  } else {
    const decimalFraction = rawNumber / 100;
    const endsWithDecimal = cleanedValue.endsWith('.');

    // the user is still typing
    if (endsWithDecimal) {
      currentTarget.value = `${Number(wholeNumber).toLocaleString('en-US')}${
        !maximumFractionDigits ? '' : `.${fraction}`
      }%`;
    } else {
      // TODO: create new options in formatPercentage; e.g. formatPercent(decimalFraction, 'MinTwoFractionDigits')
      currentTarget.value = decimalFraction.toLocaleString('en-US', {
        style: 'percent',
        minimumFractionDigits: fraction.length,
      });
    }

    if (isDecimalFraction) {
      const precision = maximumFractionDigits + wholeNumber.length + 1;
      hiddenField.value = precisionRound(decimalFraction, precision).toString(); // e.g. 5.05 / 100 === 0.050499999999999996
    } else {
      hiddenField.value = rawNumber.toString();
    }
  }
}

// TODO: move to utilities (see "Better implementations": https://stackoverflow.com/questions/11832914/how-to-round-to-at-most-2-decimal-places-if-necessary#answer-48764436)
function precisionRound(number, precision) {
  const exponentialNotation = Math.round(+`${number}e${precision}`);
  return Number(`${exponentialNotation}e${-precision}`);
}
